<template>
  <section class="mib show-on-load" :class="classes">
    <div :class="{ container: !isFullWidth }">
      <div :class="{ row: !isFullWidth }">
        <div class="col-12">
          <div class="mib__container">
            <div class="mib__background">
              <base-image
                class="mib__picture"
                :mobile-url="fields.imageMobile?.value?.src"
                :tablet-url="fields.imageTablet?.value?.src"
                :desktop-url="fields.imageDesktop?.value?.src"
                :mobile-alt="fields.imageMobile?.value?.alt"
                :tablet-alt="fields.imageTablet?.value?.alt"
                :desktop-alt="fields.imageDesktop?.value?.alt"
                :full-width="isFullWidth"
                :lazy-loading="componentRouteIndex > 1 ? 'lazy' : null"
                :fetchpriority="componentRouteIndex > 1 ? 'low' : 'high'"
                :preload-image-name="`MultipurposeImageBanner--${params.backgroundType}`"
              />
            </div>
            <div class="mib__text">
              <sc-text
                class="mib__heading"
                :field="fields.heading"
                :tag="params.headingType ?? 'h2'"
              />
              <sc-text
                class="mib__subheading"
                :class="[jssStore.brandName === 'Belhaven' && 'font-s']"
                :field="fields.subheading"
                tag="span"
              />
              <sc-rich-text
                v-track-rich-text-links="'multipurpose_image_banner'"
                class="mib__desc"
                :field="fields.description"
                tag="p"
              />
              <slot name="description" />
              <div
                v-if="
                  fields.firstLink?.value?.text ||
                  fields.secondLink?.value?.text ||
                  ($isPageEditing && rendering?.dataSource)
                "
                class="mib__buttons"
              >
                <base-button
                  v-if="fields.firstLink?.value?.text || $isPageEditing"
                  :link="fields.firstLink?.value"
                  type="link"
                  data-test-id="mib-first-link"
                  @click="
                    () => handleClickButton(fields.firstLink?.value?.text, fields.firstLink?.value)
                  "
                />
                <base-button
                  v-if="fields.secondLink?.value?.text || $isPageEditing"
                  :link="fields.secondLink?.value"
                  type="link"
                  variant="secondary"
                  data-test-id="mib-second-link"
                  @click="
                    () =>
                      handleClickButton(fields.secondLink?.value?.text, fields.secondLink?.value)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Text as ScText, RichText as ScRichText } from '@sitecore-jss/sitecore-jss-vue';
import BaseImage from '../2_elements/BaseImage/BaseImage.vue';
import vTrackRichTextLinks from '../../directives/vTrackRichTextLinks';
import BaseButton from '../2_elements/BaseButton/BaseButton.vue';
import { useButtonsAndLinksTracking } from '../../data-layer/hooks/useButtonsAndLinksTracking';
import { useRouteData } from '../../composables/useRouteData';
import { useJssStore } from '../../stores/jssStore';
import type { LinkValue } from '@/types';
import type { MultipurposeImageBannerProps } from './types';

const props = withDefaults(defineProps<MultipurposeImageBannerProps>(), {
  fields: () => ({}),
  params: () => ({}),
});

const jssStore = useJssStore();

const { componentRouteIndex } = useRouteData(props.rendering?.uid);

const { trackLinkState } = useButtonsAndLinksTracking({
  componentName: 'multipurpose_image_banner',
});

const isVarDark = computed(
  () =>
    props.params.backgroundType === 'full' ||
    (['HungryHorse', 'ChefBrewer', 'Belhaven', 'PubPartners'].includes(jssStore.brandName) &&
      props.params.alternativeBackground === '1' &&
      props.params.backgroundType === 'split')
);

const isFullWidth = computed(() => props.params.fullWidth === '1');

const classes = computed(() => [
  isFullWidth.value && 'mib--full-width',
  props.params.backgroundOverlay === '1' && 'mib--bg-overlay',
  props.params.marginBottom === '1' && 'mib--margin-btm',
  !!props.params.textPosition && `mib--text-${props.params.textPosition}`,
  !!props.params.mobileTextPosition && `mib--text-mob-${props.params.mobileTextPosition}`,
  !!props.params.backgroundType && `mib--bg-${props.params.backgroundType}`,
  props.params.alternativeBackground === '1' && 'mib--alternative-background',
  props.params.topSubheading === '1' && 'mib--top-subheading',
  isVarDark.value && 'var-dark',
  jssStore.brandName === 'ChefBrewer' && 'bottom-green-border top-green-border',
  jssStore.brandName === 'Belhaven' && isFullWidth.value && 'blh-full-width',
]);

const handleClickButton = (action?: string, href?: LinkValue) => {
  if (props.dataLayerFn) {
    props.dataLayerFn(action, href);
    return;
  }
  trackLinkState(action);
};
</script>

<style lang="scss">
@import './scss/MultipurposeImageBanner.scss';
</style>
